/**
 * Created by katarinababic on 14.5.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { runInAction } from 'mobx';
import { AlertModal } from '../../AlertModal';
import { useRootStore } from '../../../Store/useRootStore';
import { useLanguage } from '../../../Hooks/useLanguage';
import { useLocalized } from '../../../Hooks/useLocalized';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { CoachProgram, CoachProgramApi } from '../../../Model/Coach/Program/CoachProgram';
import { EndWorkoutOverviewHeader } from './EndWorkoutOverviewHeader';
import { EndWorkoutOverviewSetsListItem } from './EndWorkoutOverviewSetsListItem';
import { Button } from '../../Button';
import styled from '@emotion/styled';
import { Theme } from '../../../Theme/Theme';
import defaultImg from '../../../Assets/Images/logo.png';
import { createWorkoutLogAnalytics } from '../../../Model/WorkoutLog/WorkoutLogAnalytics';
import { ActivityLogApi } from '../../../Model/Activity/ActivityLog';

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${Theme.Colors.black};
`;

const DeleteButton = styled.button`
  background: ${Theme.Colors.lightestGrey};
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 20px;
  & > i {
    font-size: 20px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Theme.Colors.error};
  cursor: pointer;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${Theme.Colors.overlay};
  display: flex;
  z-index: 1000;
  justify-content: center;
  align-items: center;
`;

const ModalContainer = styled.div`
  position: relative;
  background: ${Theme.Colors.white};
  border-radius: 8px;
  padding: 16px;
  width: 1000px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

const ExercisesWrapper = styled.div`
  background: ${Theme.Colors.white};
  padding: 16px 0;
  margin-top: 16px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const StepItem = styled.div`
  display: flex;
  /* allow connectors to stretch full height */
  align-items: stretch;
  padding: 8px 16px;
`;

const StepIconContainer = styled.div`
  width: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 16px;
`;

const StepCircle = styled.div`
  width: 24px;
  height: 24px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepConnector = styled.div`
  flex: 1;
  width: 2px;
  background: ${Theme.Colors.lightestGrey};
  margin-top: 4px;
`;

const ExerciseImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 16px;
`;

const ExerciseInfo = styled.div`
  flex: 1;
`;

const ExerciseName = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${Theme.Colors.black};
  margin-bottom: 8px;
`;

export type EndWorkoutOverviewModalProps = {};

export const EndWorkoutOverviewModal: React.FC<EndWorkoutOverviewModalProps> = observer((props) => {
  const navigate = useNavigate();
  const { workoutViewStore, engagement, analytics: analyticsStore } = useRootStore();
  const { language } = useLanguage();
  const { workoutLog } = workoutViewStore;
  const { t } = useTrans();
  const workoutTitle = useLocalized(workoutLog?.workout?.name);

  const [processing, setProcessing] = useState(false);
  const [fitnessProgram, setFitnessProgram] = useState<CoachProgram | undefined>();
  const [isDiscardAlertOpen, setDiscardAlertOpen] = useState(false);
  const analytics = React.useMemo(() => createWorkoutLogAnalytics(workoutLog), [workoutLog]);
  const wasLastWorkout = workoutLog?.microCycleDay?.isLastMicroCycleDay === true;

  useEffect(() => {
    if (workoutLog?.coachProgramId) {
      CoachProgramApi.get(workoutLog?.coachProgramId).then((res) => setFitnessProgram(res));
    }
  }, [workoutLog]);

  const handleSave = React.useCallback(() => {
    setProcessing(true);
    if (!workoutLog) {
      setProcessing(false);
      return;
    }
    // close overview modal immediately before showing points
    runInAction(() => {
      workoutViewStore.endWorkoutOverviewModalVisible = false;
    });
    engagement
      .sendActivityLogged(ActivityLogApi.fromWorkoutLog(workoutLog))
      .then((res) => engagement.showPointsModalPromise(res))
      .then(() => workoutViewStore.terminateAndSave())
      .then(() => workoutViewStore.syncWorkoutLogWithHealthKit(workoutLog))
      .then((log) => {
        if (wasLastWorkout) {
          analyticsStore.logEvent('fitness_program_completed', {
            programId: fitnessProgram?.id,
            programTemplateId: fitnessProgram?.superMacroTemplateId,
          });
        }
        runInAction(() => {
          workoutViewStore.endWorkoutOverviewModalVisible = false;
        });
        navigate('/move', { replace: true });
        return log;
      })
      .finally(() => setProcessing(false));
  }, [engagement, workoutLog, wasLastWorkout, analyticsStore, fitnessProgram, workoutViewStore, navigate]);

  const handleDiscard = React.useCallback(() => {
    setDiscardAlertOpen(true);
  }, []);

  const confirmDiscard = React.useCallback(() => {
    setDiscardAlertOpen(false);
    if (!workoutLog) return;
    workoutLog.remove().catch(() => {}).finally(() => {
      runInAction(() => {
        workoutViewStore.endWorkoutOverviewModalVisible = false;
      });
      navigate('/move', { replace: true });
    });
  }, [workoutLog, workoutViewStore, navigate]);

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{workoutTitle}</ModalTitle>
          <DeleteButton onClick={handleDiscard}>
            <i className="mdi mdi-delete" />
          </DeleteButton>
        </ModalHeader>
        <EndWorkoutOverviewHeader analytics={analytics} />
        <ExercisesWrapper>
          {workoutLog?.exerciseBlocks.map((block, idx, arr) => {
            const imgSrc = block.exercise.firstSmallestMediaUrl || defaultImg;
            const isLast = idx === arr.length - 1;
            return (
              <StepItem key={block.exerciseBlockId || block.blockIndex}>
                <StepIconContainer>
                  <StepCircle>
                    <i className="mdi mdi-check" style={{ color: Theme.Colors.primary }} />
                  </StepCircle>
                  {!isLast && <StepConnector />}
                </StepIconContainer>
                <ExerciseImage src={imgSrc} alt={block.exercise.name} />
                <ExerciseInfo>
                  <ExerciseName>{block.exercise.localeName(language)}</ExerciseName>
                  {block.sets.map((set, sidx) => (
                    <EndWorkoutOverviewSetsListItem key={set.setId || sidx} set={set} index={sidx} />
                  ))}
                </ExerciseInfo>
              </StepItem>
            );
          })}
        </ExercisesWrapper>
        <ButtonWrapper>
          <Button
            value={t('endWorkoutOverview.saveWorkout', { defaultValue: 'Save workout' })}
            onClick={handleSave}
            disabled={processing}
          />
        </ButtonWrapper>
      </ModalContainer>
      <AlertModal
        isOpen={isDiscardAlertOpen}
        title={t('endWorkoutOverview.discard.alert.title', { defaultValue: 'Discard workout?' })}
        body={t('endWorkoutOverview.discard.alert.message', {
          defaultValue: 'Do you want to discard this workout and not save it?',
        })}
        cancelText={
          t('endWorkoutOverview.discard.alert.keep', { defaultValue: 'No, keep Workout' }) ?? 'No, keep Workout'
        }
        confirmText={
          t('endWorkoutOverview.discard.alert.discard', { defaultValue: 'Delete Workout' }) ?? 'Delete Workout'
        }
        onCancel={() => setDiscardAlertOpen(false)}
        onConfirm={confirmDiscard}
      />
    </ModalOverlay>
  );
});
